import { apiClient } from '@/lib/api-client';

// ── Chat API types ──────────────────────────────────────

export interface ChatParticipant {
    id: string;
    fullName: string;
    email: string;
}

/**
 * A chat as seen by the UI (post-flatten). Anchored to a Client; may also be
 * narrowed to a specific Project (intake / estimate / project / proposal).
 */
export interface Chat {
    id: string;
    // Client anchor — required for new chats; may be empty string on legacy
    // rows that pre-date the client-anchor model and still wait to be backfilled.
    clientId: string;
    clientName: string;
    // Project association is OPTIONAL.
    projectId: string;
    projectLabel: string;
    otherParticipant: ChatParticipant;
    // Populated on group chats; absent for 1-on-1. Includes the current user.
    participants?: ChatParticipant[];
    isGroup?: boolean;
    chatName?: string;
    lastMessage: string;
    lastMessageAt: string;
    lastMessageCreatedAt: string;
    isLastMessageFromMe: boolean;
    unreadCount: number;
    createdAt: string;
    updatedAt: string;
}

export interface GetChatsParams {
    clientId?: string;
    projectId?: string;
}

export interface ChatMessage {
    id: string;
    chatId: string;
    sender: {
        id: string;
        fullName: string;
        email: string;
    };
    content: string;
    attachments: any[];
    createdAt: string;
    updatedAt: string;
    isRead: boolean;
    readAt?: string;
}

export interface GroupedMessages {
    date: string;
    messages: ChatMessage[];
}

// Backend can return either of these shapes from GET /chat. The new
// client-anchored shape is preferred; the legacy project-grouped shape is
// retained for back-compat during the rollout (and is what older deploys
// of the backend may still emit). We normalize both into a flat Chat[].
interface ClientGroup {
    clientId?: string;
    clientName?: string;
    chats: Partial<Chat>[];
}
interface ProjectGroup {
    projectId?: string;
    projectLabel?: string;
    projectName?: string;
    chats: Partial<Chat>[];
}
interface GroupedChatsResponse {
    clients?: ClientGroup[];   // new shape (client-anchored)
    projects?: ProjectGroup[]; // legacy shape (project-grouped)
}

/**
 * Normalize a raw chat (in any returned shape) into a complete Chat with
 * sensible defaults. The function never throws — missing fields become
 * empty strings / zeros so the UI can still render something rather than
 * silently crashing.
 */
function normalizeChat(raw: Partial<Chat>, fallbacks: Partial<Chat> = {}): Chat {
    return {
        id: raw.id ?? '',
        clientId: raw.clientId ?? fallbacks.clientId ?? '',
        clientName: raw.clientName ?? fallbacks.clientName ?? '',
        projectId: raw.projectId ?? fallbacks.projectId ?? '',
        projectLabel: raw.projectLabel ?? fallbacks.projectLabel ?? '',
        otherParticipant: raw.otherParticipant ?? { id: '', fullName: '', email: '' },
        participants: raw.participants,
        isGroup: raw.isGroup,
        chatName: raw.chatName,
        lastMessage: raw.lastMessage ?? '',
        lastMessageAt: raw.lastMessageAt ?? '',
        lastMessageCreatedAt: raw.lastMessageCreatedAt ?? '',
        isLastMessageFromMe: raw.isLastMessageFromMe ?? false,
        unreadCount: raw.unreadCount ?? 0,
        createdAt: raw.createdAt ?? '',
        updatedAt: raw.updatedAt ?? '',
    };
}

// ── Chat API methods ────────────────────────────────────

export const chatApi = {
    /**
     * Create or get an existing 1-on-1 chat. Anchored to a Client. Project is
     * optional; when supplied it must belong to the same client (server-enforced).
     */
    createChat: async (
        clientId: string,
        participant2Id: string,
        projectId?: string | null,
    ): Promise<Chat> => {
        const body: Record<string, any> = { clientId, participant2Id };
        if (projectId) body.projectId = projectId;
        const res = await apiClient.post<Partial<Chat>>('/chat', body);
        return normalizeChat(res, { clientId, projectId: projectId ?? '' });
    },

    /**
     * Create a group chat (3+ participants). Anchored to a Client; project optional.
     */
    createGroupChat: async (
        clientId: string,
        participantIds: string[],
        opts: { projectId?: string | null; chatName?: string } = {},
    ): Promise<Chat> => {
        const body: Record<string, any> = { clientId, participantIds };
        if (opts.projectId) body.projectId = opts.projectId;
        if (opts.chatName) body.chatName = opts.chatName;
        const res = await apiClient.post<Partial<Chat>>('/chat/group', body);
        return normalizeChat(res, { clientId, projectId: opts.projectId ?? '' });
    },

    /**
     * Get all chats for the authenticated user.
     *
     * Server may return one of three shapes:
     *   { clients: [{ clientId, clientName, chats }] }   ← new client-anchored
     *   { projects: [{ projectId, projectLabel, chats }] } ← legacy project-grouped
     *   Chat[]                                             ← hypothetical flat (forward-compat)
     *
     * We normalize all three to a flat Chat[]. Each chat carries its own
     * clientId/clientName/projectId/projectLabel; if missing on the chat item,
     * we backfill from the parent group. This keeps wire format isolated to
     * this file and lets the UI consume one stable shape forever.
     */
    getChats: async (params: GetChatsParams = {}): Promise<Chat[]> => {
        const searchParams = new URLSearchParams();
        if (params.clientId) searchParams.append('clientId', params.clientId);
        if (params.projectId) searchParams.append('projectId', params.projectId);

        const queryString = searchParams.toString();
        const url = queryString ? `/chat?${queryString}` : '/chat';

        const response = await apiClient.get<GroupedChatsResponse | Chat[]>(url);

        // Forward-compat: flat array.
        if (Array.isArray(response)) {
            return response.map((c) => normalizeChat(c));
        }

        // New shape (preferred): grouped by client.
        if (response && Array.isArray(response.clients)) {
            return response.clients.flatMap((g) =>
                Array.isArray(g.chats)
                    ? g.chats.map((c) =>
                          normalizeChat(c, {
                              clientId: g.clientId,
                              clientName: g.clientName,
                          }),
                      )
                    : [],
            );
        }

        // Legacy shape: grouped by project.
        if (response && Array.isArray(response.projects)) {
            return response.projects.flatMap((g) =>
                Array.isArray(g.chats)
                    ? g.chats.map((c) =>
                          normalizeChat(c, {
                              projectId: g.projectId,
                              projectLabel: g.projectLabel,
                          }),
                      )
                    : [],
            );
        }

        // Truly unknown — warn loudly so future contract drift is easy to spot.
        console.warn('chatApi.getChats: unexpected response shape', response);
        return [];
    },

    /**
     * Get messages for a specific chat, grouped by date.
     */
    getMessages: async (chatId: string): Promise<GroupedMessages[]> => {
        const response = await apiClient.get<GroupedMessages[]>(`/chat/${chatId}/messages/grouped`);
        return response;
    },

    /**
     * Create or get a general / team chat (no client, no project anchor).
     * Used by the Team Chat tab where two users chat about non-project topics.
     */
    createGeneralChat: async (participant2Id: string): Promise<Chat> => {
        const res = await apiClient.post<Partial<Chat>>('/chat', { participant2Id });
        return normalizeChat(res);
    },

    /**
     * #4 — Total unread chat-message count for the current user, across every
     * chat they can see. Powers the left-nav badge and dashboard Priority Alerts.
     */
    getUnreadCount: async (): Promise<number> => {
        const res = await apiClient.get<{ unreadCount: number }>('/chat/unread-count');
        return res?.unreadCount ?? 0;
    },
};
